import classNames from 'classnames'
import { VFC, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { type MenuOption } from '@opallabs/concepts'
import { GemColors } from '@opallabs/gem'
import { ColorLabelToken } from '../../ColorToken'
import { Tooltip } from '../../Tooltip'
import * as styles from '../styles'

type SelectionListProps = {
  className?: string
  options: MenuOption[]
  items: MenuOption[]
  sortSelectionList?: boolean
  colorsEnabled?: boolean
}

const getHorizontalOffset = (element: Element) => {
  // @note This implementation is only correct for the default `writing-mode` (`horizontal-tb`) and LTR layout.
  const { width, left } = element.getBoundingClientRect()
  return width + left
}

const getOverflowLabel = (items: MenuOption[], overflowLength: number) => {
  return items
    .slice(-overflowLength)
    .reduce((acc, cur, idx) => (acc += (idx !== 0 ? ', ' : '') + cur.label), '')
}

export const SelectionList: VFC<SelectionListProps> = ({
  className,
  options,
  items,
  sortSelectionList,
  colorsEnabled = false,
}) => {
  const listRef = useRef<HTMLUListElement>(null)
  const [overflowCount, setOverflowCount] = useState(0)
  const [overflowLabel, setOverflowLabel] = useState('')
  const { width = 0 } = useResizeDetector({
    refreshMode: 'throttle',
    refreshRate: 50,
    targetRef: listRef,
  })

  const tokenMaxWidth = useMemo(() => {
    if (!width || !items.length) return 100

    // Subtract space for overflow count from available width
    const overflowAdjustment = overflowCount > 0 ? 40 : 0
    if (items.length <= overflowCount) return 100
    const max = Math.floor(
      (width - overflowAdjustment) / (items.length - overflowCount)
    )

    return Math.max(100, max)
  }, [width, items, overflowCount])

  const sortedItems = useMemo(
    () =>
      sortSelectionList
        ? items.sort((a, b) => a.label.localeCompare(b.label))
        : items,
    [items, sortSelectionList]
  )

  const availableOptionIds = useMemo(
    () => options.map((option) => option.value),
    [options]
  )

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list?.children.length) return

    const calculateOverflow = () => {
      const parentOffset = getHorizontalOffset(list)
      const children = Array.from(list.children).filter(
        (el) => !(el as HTMLElement).dataset.overflow
      ) as HTMLElement[]

      children.forEach((el) => (el.style.display = ''))

      const overflowIdx =
        items.length <= 1
          ? null
          : children.findIndex((el) => getHorizontalOffset(el) > parentOffset)

      if (overflowIdx === -1 || overflowIdx === null) {
        setOverflowCount(0)
        setOverflowLabel('')
        return
      }

      children.slice(overflowIdx).forEach((el) => (el.style.display = 'none'))

      const overflowEl = list.children[list.children.length - 1]
      const finalOverflowIdx =
        getHorizontalOffset(overflowEl) > parentOffset && overflowIdx > 0
          ? overflowIdx - 1
          : overflowIdx

      if (finalOverflowIdx < overflowIdx && finalOverflowIdx >= 0) {
        children[finalOverflowIdx].style.display = 'none'
      }

      const overflowLength = items.length - finalOverflowIdx
      setOverflowCount(overflowLength)
      setOverflowLabel(getOverflowLabel(items, overflowLength))
    }

    calculateOverflow()
    const frameId = requestAnimationFrame(calculateOverflow)
    return () => cancelAnimationFrame(frameId)
  }, [items, width])

  return (
    <ul
      css={styles.selectionList}
      className={classNames('lapidary-selection-list', className)}
      ref={listRef}
    >
      {sortedItems.map((item) => {
        const isUnavailable = !availableOptionIds.includes(item.value)
        return (
          <li
            key={item.value}
            style={{
              maxWidth: tokenMaxWidth,
            }}
          >
            <ColorLabelToken
              aria-invalid={isUnavailable}
              color={colorsEnabled && item.color ? item.color : GemColors.N100}
              label={item.label}
            />
          </li>
        )
      })}
      {/* useLayoutEffect recalculates overflowCount to dynamically show the count value */}
      {overflowCount > 0 && (
        <Tooltip
          css={{ whiteSpace: 'normal' }}
          content={overflowLabel}
          sideOffset={4}
        >
          <li data-overflow>
            <ColorLabelToken
              color={GemColors.N100}
              label={`+${overflowCount}`}
            />
          </li>
        </Tooltip>
      )}
    </ul>
  )
}
SelectionList.displayName = 'Lapidary: SelectionList ← MultiCheckSelect'
